/**
 * ui-store.ts —— 界面状态（内存，不持久化）
 *
 * 刻意不放进 IndexedDB：这些状态刷新页面后应该归零，
 * 持久化反而会造成"上次打开的会话这次自动弹出来"这类意外。
 * 唯一跨会话记住的是 lastTab，那个放在 settings 层。
 */

import { Store } from './store';
import type { TabKey, ModalLevel, OverlayKind } from '@/types/events';
import type { EvalResult } from '@/types/il';

export interface ModalState {
  open: boolean;
  level: ModalLevel;
  title?: string;
  text: string;
  results?: EvalResult[];
  /** 确认回调。存在即表示这是一次需要用户点头的操作 */
  onConfirm?: () => void;
  /**
   * 取消回调。与 onConfirm 成对出现：
   * 调用方常常在等一个「同意 / 拒绝」的决定（如求值器的二次确认），
   * 只有 onConfirm 的话，用户点「取消」时那个等待会永远挂着。
   */
  onCancel?: () => void;
}

export interface OverlayEntry {
  kind: OverlayKind;
  /** 承载的具体 id：chat → sessionId，digest → digestId，voice → '' */
  refId: string;
}

export interface UiState {
  tab: TabKey;
  /** 覆盖层栈，栈顶为当前活动层 */
  overlays: OverlayEntry[];
  modal: ModalState;
  /** AI 请求进行中 */
  busy: boolean;
}

export const uiStore = new Store<UiState>({
  tab: 'home',
  overlays: [],
  modal: { open: false, level: 'info', text: '' },
  busy: false,
});

/* ---------- 覆盖层操作的便捷封装 ---------- */

export function pushOverlay(entry: OverlayEntry): void {
  const cur = uiStore.get().overlays;
  // 同种同目标的层不重复压栈，避免连点打开出多层
  if (cur.some((o) => o.kind === entry.kind && o.refId === entry.refId)) return;
  uiStore.set({ overlays: [...cur, entry] });
}

export function popOverlay(): void {
  const cur = uiStore.get().overlays;
  if (cur.length === 0) return;
  uiStore.set({ overlays: cur.slice(0, -1) });
}

export function popAllOverlays(): void {
  if (uiStore.get().overlays.length === 0) return;
  uiStore.set({ overlays: [] });
}

export function topOverlay(): OverlayEntry | undefined {
  const cur = uiStore.get().overlays;
  return cur[cur.length - 1];
}
